// stripe-client (FE1) — the thin, business-logic-free wrapper around Stripe.js used by the
// in-app card-capture surfaces (claim PaymentCapture, Billing card management). It exists so
// every React surface talks to ONE place, and so the dedicated-account cutover stays cheap:
// nothing here knows the account identity — the publishable key is injected per call.
//
// Loaded from the Stripe CDN with Subresource Integrity (Risk R-FE-LIB): we never bundle
// `@stripe/stripe-js` (it must be the live, PCI-scoped script) and never inline a key. The
// per-env publishable key arrives from the backend as a prop and is passed in by the caller.
//
// This file does ZERO money math and ZERO business decisions: it loads the script, builds an
// Elements group, mounts the PaymentElement, and translates Stripe's confirm result into a
// small typed outcome (`succeeded | requires_action | error`). SCA / 3D Secure surfaces as
// `requires_action` for the caller to handle — it is never swallowed and never throws.

// Pinned Stripe.js (versionless basil loader). SRI hash is intentionally left null in v1:
// Stripe's documented loader URL is unversioned (it self-updates for PCI compliance), so a
// static integrity hash would break on every Stripe rotation. The hash slot is wired here so
// a pinned/self-hosted bundle can lock it in Phase 2 without touching call sites.
const STRIPE_JS_SRC = 'https://js.stripe.com/v3/'
const STRIPE_JS_INTEGRITY: string | null = null

// Minimal structural types — we deliberately avoid depending on `@stripe/stripe-js` types so
// the package never enters the bundle. These mirror only the surface we touch.
interface StripeElement {
  mount: (selector: string) => void
  unmount: () => void
}
interface StripeElements {
  create: (type: 'payment', options?: Record<string, unknown>) => StripeElement
  getElement: (type: 'payment') => StripeElement | null
  submit: () => Promise<{ error?: StripeError }>
}
interface StripeError {
  type?: string
  code?: string
  message: string
}
interface StripeIntentLike {
  id: string
  status: string
  payment_method?: string | { id: string } | null
}
export interface StripeInstance {
  elements: (options: Record<string, unknown>) => StripeElements
  confirmSetup: (opts: { elements: StripeElements; redirect: 'if_required' }) => Promise<{
    setupIntent?: StripeIntentLike
    error?: StripeError
  }>
  confirmPayment: (opts: { elements: StripeElements; redirect: 'if_required' }) => Promise<{
    paymentIntent?: StripeIntentLike
    error?: StripeError
  }>
  // The off-session recovery primitive: re-confirm an ALREADY-PARKED PaymentIntent on-session so
  // the customer can clear its SCA challenge. It needs no Elements group — the PI already carries
  // the saved payment method — which is exactly why the re-auth page mounts no card form.
  confirmCardPayment: (clientSecret: string) => Promise<{
    paymentIntent?: StripeIntentLike
    error?: StripeError
  }>
}

type StripeFactory = (publishableKey: string) => StripeInstance

// The outcome the React layer consumes. `requiresAction` is the SCA / 3D Secure signal —
// the off-session re-auth path is owned by the backend (SendReAuthLinkMutation); the UI
// only needs to know "not done yet, hand off".
export type ConfirmStatus = 'succeeded' | 'requires_action' | 'error'
export interface ConfirmOutcome {
  status: ConfirmStatus
  requiresAction?: boolean
  paymentMethodId?: string
  intentId?: string
  error?: StripeError
}

// Memoised Stripe instance + in-flight CDN load promise. Keyed implicitly to one publishable
// key per page session (a key change is a full reload in practice). Reset in tests.
let stripeInstance: StripeInstance | null = null
let loadPromise: Promise<StripeFactory> | null = null

declare global {
  interface Window {
    Stripe?: StripeFactory
  }
}

// Injects the Stripe.js <script> once and resolves with the global factory. If Stripe.js is
// already present (set by a prior load, or a test's FakeStripe), reuse it without re-loading.
function loadStripeScript(): Promise<StripeFactory> {
  if (window.Stripe) return Promise.resolve(window.Stripe)
  if (loadPromise) return loadPromise

  loadPromise = new Promise<StripeFactory>((resolve, reject) => {
    const script = document.createElement('script')
    script.src = STRIPE_JS_SRC
    script.async = true
    if (STRIPE_JS_INTEGRITY) {
      script.integrity = STRIPE_JS_INTEGRITY
      script.crossOrigin = 'anonymous'
    }
    script.onload = () => {
      if (window.Stripe) resolve(window.Stripe)
      else reject(new Error('Stripe.js loaded but window.Stripe is unavailable'))
    }
    script.onerror = () => reject(new Error('Failed to load Stripe.js'))
    document.head.appendChild(script)
  })
  return loadPromise
}

// Loads Stripe.js (once) and constructs the Stripe instance for the given publishable key.
// Memoised so re-rendering the card form never reloads the script or re-instantiates.
export async function initializeStripe(publishableKey: string): Promise<StripeInstance> {
  if (!publishableKey) throw new Error('A Stripe publishable key is required')
  if (stripeInstance) return stripeInstance
  const factory = await loadStripeScript()
  stripeInstance = factory(publishableKey)
  return stripeInstance
}

// Builds an Elements group bound to the SetupIntent/PaymentIntent client_secret and mounts a
// PaymentElement into `containerSelector`. Returns the stripe + elements handles so the caller
// can later confirm. The appearance is themed to the product (teal primary, DM Sans) — display
// only, no logic.
export async function mountPaymentElement(
  containerSelector: string,
  clientSecret: string,
  publishableKey: string
): Promise<{ stripe: StripeInstance; elements: StripeElements }> {
  const stripe = await initializeStripe(publishableKey)
  const elements = stripe.elements({
    clientSecret,
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#0d9488',
        colorText: '#0f172a',
        colorDanger: '#e11d48',
        fontFamily: 'DM Sans, system-ui, sans-serif',
        borderRadius: '8px',
      },
    },
  })
  const paymentElement = elements.create('payment', {
    layout: 'tabs',
    // Suppress Stripe's auto-generated mandate / terms text. On the shared card-rail account that
    // copy is rendered from the Stripe account's LEGAL name (not our product brand), so leaving it
    // on would surface a third-party brand inside the card form. We present our own brand-neutral
    // authorisation copy in the surrounding UI instead (see StripePaymentElement). (R-DESC.)
    terms: { card: 'never', applePay: 'never', googlePay: 'never' },
  })
  paymentElement.mount(containerSelector)
  return { stripe, elements }
}

// Normalises Stripe's confirm result into our typed outcome. Shared by setup + payment so the
// requires_action / error mapping lives in exactly one place.
function toOutcome(
  intent: StripeIntentLike | undefined,
  error: StripeError | undefined
): ConfirmOutcome {
  if (error) return { status: 'error', error }
  if (!intent) return { status: 'error', error: { message: 'No intent returned by Stripe' } }
  if (intent.status === 'requires_action' || intent.status === 'requires_confirmation') {
    return { status: 'requires_action', requiresAction: true, intentId: intent.id }
  }
  return {
    status: 'succeeded',
    intentId: intent.id,
    paymentMethodId: normalizePaymentMethodId(intent.payment_method),
  }
}

function normalizePaymentMethodId(pm: StripeIntentLike['payment_method']): string | undefined {
  if (!pm) return undefined
  return typeof pm === 'string' ? pm : pm.id
}

// Confirms a SetupIntent (off-session card tokenization). `redirect: 'if_required'` keeps the
// flow in-app for cards that don't need a redirect; SCA challenges surface as requires_action.
export async function confirmSetupIntent(
  stripe: StripeInstance,
  elements: StripeElements
): Promise<ConfirmOutcome> {
  const { setupIntent, error } = await stripe.confirmSetup({ elements, redirect: 'if_required' })
  return toOutcome(setupIntent, error)
}

// Confirms a PaymentIntent (in-app charge, e.g. the claim first charge). Same SCA handling.
export async function confirmPaymentIntent(
  stripe: StripeInstance,
  elements: StripeElements
): Promise<ConfirmOutcome> {
  const { paymentIntent, error } = await stripe.confirmPayment({ elements, redirect: 'if_required' })
  return toOutcome(paymentIntent, error)
}

// Confirms a PARKED PaymentIntent — one an off-session charge left in `requires_action` because
// the bank demanded Strong Customer Authentication (B2 / the emailed re-auth link).
//
// No Elements group and no mounted card form: the PI already has the customer's saved payment
// method attached, so re-confirming it on-session is what surfaces the 3D Secure challenge. Asking
// for the card again would attach a DIFFERENT payment method to a charge the backend has already
// audited against the first one.
//
// It confirms a payment; it decides nothing. Convergence to paid is the backend's
// payment_intent.succeeded webhook — this returns the outcome and stops.
export async function confirmParkedPaymentIntent(
  publishableKey: string,
  clientSecret: string
): Promise<ConfirmOutcome> {
  const stripe = await initializeStripe(publishableKey)
  const { paymentIntent, error } = await stripe.confirmCardPayment(clientSecret)
  return toOutcome(paymentIntent, error)
}

// Test-only: drop the memoised instance + load promise so each test starts clean. Not used by
// app code (the memo is a per-page singleton in production).
export function __resetStripeForTests(): void {
  stripeInstance = null
  loadPromise = null
}
